import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { isClickedOutside, lockScroll, onAnimationEnd, unlockScroll } from '@theme/utilities';

/*
 * Shared dimmed backdrop for non-modal drawers.
 *
 * Drawers used to open with dialog.showModal(), which gives a ::backdrop and a focus trap for free
 * — but a modal <dialog> marks EVERY element outside it `inert`, and the browser then recomputes
 * style for the whole document on both open AND close. Measured on a 44-card collection: showModal()
 * = 723ms of style recalc, show() = 2ms; it scaled with page size and was the single biggest piece
 * of mobile INP. So drawers now open with show() (no inert pass) and we recreate what modal gave us:
 * this one fixed backdrop element for the dim + click-to-close, plus the theme's own scroll-lock and
 * focus-trap. A11y note: the background is no longer natively `inert` (that was the expensive part);
 * pointer is blocked by the backdrop and keyboard by the focus trap, but an AT virtual cursor can
 * still reach the background — an accepted trade for ~720ms per open.
 */
let drawerBackdrop = null;
const drawerOrder = (d) => Number(d.style.getPropertyValue('--drawer-stack-order') || 0);

function ensureDrawerBackdrop() {
  if (drawerBackdrop) return drawerBackdrop;
  drawerBackdrop = document.createElement('div');
  drawerBackdrop.className = 'theme-drawer__backdrop';
  drawerBackdrop.setAttribute('data-drawer-backdrop', '');
  drawerBackdrop.addEventListener('click', () => {
    const open = [...document.querySelectorAll('theme-drawer[open]')];
    if (!open.length) return;
    const top = open.reduce((a, b) => (drawerOrder(b) > drawerOrder(a) ? b : a));
    /** @type {any} */ (top).close?.();
  });
  document.body.appendChild(drawerBackdrop);
  return drawerBackdrop;
}

/** Show the backdrop just below the topmost open drawer, or hide it when none are open. */
function syncDrawerBackdrop() {
  const open = [...document.querySelectorAll('theme-drawer[open]')];
  if (!open.length) {
    drawerBackdrop?.classList.remove('is-active');
    return;
  }
  const el = ensureDrawerBackdrop();
  const top = open.reduce((a, b) => (drawerOrder(b) > drawerOrder(a) ? b : a));
  const panel = top.querySelector('.theme-drawer__dialog');
  const z = panel ? parseInt(getComputedStyle(panel).zIndex, 10) : NaN;
  if (Number.isFinite(z)) el.style.zIndex = String(z - 1);
  el.classList.add('is-active');
}

/**
 * A drawer that opens from the right side.
 *
 * Drawers ALWAYS open as overlays with a dimmed backdrop, on every viewport.
 * They open NON-modally — `show()`, not `showModal()`; the dim, scroll-lock and
 * focus trap are recreated by hand. See the file header for why: `showModal()`
 * inert-s the whole document and cost 723ms of style recalc against 2ms.
 * The old ≥990px mode — a non-modal sidebar that SQUEEZED the page content
 * aside — is gone: the squeeze re-laid-out the whole page on open/close
 * (visible stutter) and, with the theme back on root scrolling, an overlay
 * is both smoother and matches FAORIA's behavior.
 *
 * Dispatches {@link DrawerOpenEvent} and {@link DrawerCloseEvent}.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} panel - The dialog that owns all visual styles and animation.
 *
 * @extends {Component<Refs>}
 */
export class ThemeDrawer extends Component {
  requiredRefs = ['panel'];

  /** Monotonically increasing counter for z-index stacking of multiple open drawers. */
  static #stackOrder = 0;

  /** @type {boolean} */
  #isClosing = false;

  /** True while an open() has recorded intent and is waiting for the tap's frame to paint. */
  /** @type {boolean} */
  #isOpening = false;

  /** When true, the drawer will reopen after the current close animation finishes. */
  /** @type {{ enter?: 'inline-start' } | false} */
  #deferredOpen = false;

  /** Element to restore focus to when the drawer closes. */
  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** Always-modal: matches every viewport, never fires 'change'. */
  /** @type {MediaQueryList} */
  #modalQuery = window.matchMedia('all');

  /**
   * @returns {boolean} Whether the drawer is currently open.
   */
  get isOpen() {
    return this.hasAttribute('open');
  }

  connectedCallback() {
    super.connectedCallback();
    this.#modalQuery.addEventListener('change', this.#onModalBreakpointChange);

    // Defensive sync of the static stack counter. It only counts the opens this
    // module itself made, so a drawer that is (re)connected already carrying an
    // inline --drawer-stack-order would otherwise be invisible to it, and the next
    // open would hand out a lower order — a fresh drawer sliding UNDER one already
    // up. Nothing in the theme sets that property today — the cross-page restore
    // script that used to set it is gone (see snippets/theme-drawer.liquid, which
    // now only clears its old session key) — so the guard is here for markup that
    // reaches connectedCallback already carrying an order.
    const restoredOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    if (restoredOrder > ThemeDrawer.#stackOrder) {
      ThemeDrawer.#stackOrder = restoredOrder;
    }

    if (this.isOpen) {
      this.#onRestore();
    }
  }

  /**
   * Restore path: the inline script in theme-drawer.liquid set [open] on
   * <theme-drawer> + <dialog> before this module loaded. The dialog is
   * already visible — we just wire close listeners. We deliberately skip
   * trapFocus and focus moves: the user is loading a fresh page and
   * expects focus on main content, not inside a drawer left over from
   * the previous session.
   */
  #onRestore() {
    const { panel } = this.refs;
    // The inline restore script set [open] and called show() before this module loaded; re-lock
    // scroll, trap focus, and bring up the backdrop for the already-open drawer.
    lockScroll(panel);
    trapFocus(panel);
    syncDrawerBackdrop();

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.panel);
    this.#modalQuery.removeEventListener('change', this.#onModalBreakpointChange);
    this.#removeEventListeners();
    removeTrapFocus();
    this.#deferredOpen = false;
  }

  /**
   * Closes the drawer when the user clicks the backdrop (outside the dialog
   * content area). Only meaningful in modal mode — in sidebar mode the
   * dialog has no backdrop so this handler is inert.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #onBackdropClick = (event) => {
    const { panel } = this.refs;

    if (isClickedOutside(event, panel)) {
      if (this.#hasOpenNestedDialog()) return;

      this.close();
    }
  };

  /**
   * @returns {boolean} Whether the drawer panel contains an open dialog other than itself.
   */
  #hasOpenNestedDialog() {
    return this.#getOpenNestedDialog() !== null;
  }

  /**
   * @returns {HTMLDialogElement | null} The open dialog nested inside the drawer panel, if any.
   */
  #getOpenNestedDialog() {
    return /** @type {HTMLDialogElement | null} */ (this.refs.panel.querySelector('dialog[open]'));
  }

  /**
   * Re-opens an already-open drawer in place — close() then show(), no animation — and rebuilds
   * by hand everything that reopening drops: scroll lock, focus trap, backdrop, close listeners.
   * It exists for the days when the drawer changed shape at a breakpoint and had to be re-created
   * mid-flight without the user seeing it move.
   *
   * It does not run today: drawers are non-modal overlays on every viewport, so `#modalQuery` is
   * `matchMedia('all')` — it always matches, and a query that never stops matching never fires
   * 'change'.
   */
  #onModalBreakpointChange = () => {
    if (!this.isOpen) return;

    const { panel } = this.refs;
    const nestedDialog = this.#getOpenNestedDialog();
    const nestedActiveElement =
      nestedDialog?.contains(document.activeElement) && document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;

    // Close the current dialog mode and immediately reopen in the new mode.
    // No animation — the drawer stays visually in place.
    panel.close();
    removeTrapFocus();

    lockScroll(panel);
    panel.show();
    trapFocus(panel);
    syncDrawerBackdrop();

    if (nestedDialog?.open) {
      this.#bringNestedDialogToFront(nestedDialog, nestedActiveElement);
    } else {
      this.#focusInitialElement();
    }

    // Ensure keydown and click listeners are registered. The sessionStorage
    // restore path bypasses open(), so these may not have been added yet.
    // addEventListener deduplicates identical listeners, so this is safe
    // even if they were already registered.
    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  };

  /**
   * Closes the drawer when the user presses Escape.
   * preventDefault() suppresses the native dialog cancel event in modal mode.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    // isComposing: don't close while an Input Method Editor (e.g. Japanese,
    // Chinese, Korean input) consumes Escape to dismiss its candidate list —
    // can happen inside the cart's discount-code field.
    // defaultPrevented: let nested components handle Escape first.
    if (event.isComposing || event.defaultPrevented) return;
    if (!this.isOpen) return;
    if (!this.#isTopmost()) return;

    event.preventDefault();
    this.close();
  };

  /**
   * Shows the drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction. `'inline-start'` slides left-to-right (used when the chat docks).
   */
  open({ enter } = {}) {
    if (this.#isOpening) return;
    if (this.isOpen) {
      this.#bringToFront({ enter });
      return;
    }
    if (this.#isClosing) {
      this.#deferredOpen = { enter };
      return;
    }

    const { panel } = this.refs;

    // The tap's own frame stays cheap; the reveal runs after it has painted.
    //
    // Measured on the live theme at 375px, /collections/auskari?page=3: the filters button costs
    // 191-219ms of synchronous work, and ONE call accounts for 105.7ms of it — panel.show(). A
    // closed dialog is display:none, so showing it styles the drawer's entire subtree from scratch:
    // 797 elements, 76 checkboxes. Stripping every :has() rule from the page takes that same show()
    // from 105.7ms to 18.8ms, so most of it is the document-wide invalidation this theme pays for
    // any structural change — and that cannot be aimed at rule by rule (removing subsets of the 218
    // rules changed nothing; only removing all of them did). Field data agrees about where it
    // matters: this page's INP p75 is 482ms on mobile against 103ms on desktop, and a phone CPU is
    // several times slower than the machine those numbers were taken on.
    //
    // A single requestAnimationFrame does NOT get out of the way — it runs before that same frame
    // paints. Hence the nested one: the tap paints the button's own state, the drawer rises on the
    // next frame (~17-33ms later), covered by its slide-in animation.
    //
    // show(), NOT showModal(): a modal dialog inert-s the whole document and forces a document-wide
    // style recalc. The dim/scroll-lock/focus-trap that modal gave us are recreated manually — the
    // backdrop element, lockScroll, and trapFocus.
    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));
    this.#previouslyFocused = /** @type {HTMLElement | null} */ (document.activeElement);
    this.#isOpening = true;

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';

    const reveal = () => {
      const wasOpening = this.#isOpening;
      this.#isOpening = false;
      // close() during the wait cancels the reveal outright; whichever scheduler arrives second
      // finds the flag already cleared and does nothing.
      if (!wasOpening || this.isOpen || this.#isClosing || !this.isConnected) return;

      this.#reveal(panel, openClass);
    };

    // rAF is the scheduler that matters, but it does not run at all in a hidden document. A timer
    // backstop means a drawer can never end up armed and never revealed.
    requestAnimationFrame(() => requestAnimationFrame(reveal));
    setTimeout(reveal, 120);
  }

  /**
   * The actual reveal, split out so both schedulers above can call it.
   * @param {HTMLDialogElement} panel
   * @param {string} openClass
   */
  #reveal(panel, openClass) {
    lockScroll(panel);
    panel.classList.add(openClass);
    panel.show();
    trapFocus(panel);

    this.setAttribute('open', '');
    syncDrawerBackdrop();
    this.#focusInitialElement();

    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    this.dispatchEvent(new DrawerOpenEvent());

    document.addEventListener('keydown', this.#onKeyDown);
    panel.addEventListener('click', this.#onBackdropClick);
  }

  /**
   * Bumps this drawer's z-index to the top of the stack and replays
   * the slide-in animation. Called when open() is invoked on an
   * already-open drawer.
   *
   * @param {object} [options]
   * @param {'inline-start'} [options.enter] - Slide direction.
   */
  #bringToFront({ enter } = {}) {
    const { panel } = this.refs;

    ThemeDrawer.#stackOrder += 1;
    this.style.setProperty('--drawer-stack-order', String(ThemeDrawer.#stackOrder));

    // Non-modal dialogs stack by z-index (driven by --drawer-stack-order, bumped just above), so
    // there is no top-layer re-showModal() dance — just refresh the backdrop to sit under the new
    // topmost drawer.
    syncDrawerBackdrop();

    const openClass =
      enter === 'inline-start' ? 'theme-drawer__dialog--opening-inline-start' : 'theme-drawer__dialog--opening';
    panel.classList.add(openClass);
    onAnimationEnd(panel, () => panel.classList.remove(openClass), { subtree: false });

    // Re-fire theme-drawer:open. This path is an open() on an ALREADY-open drawer, so the [open]
    // attribute never changes; the listeners (header-actions, cart-drawer, r69-cart-upsell,
    // chat-drawer) key off the event, and without this they would miss the re-open entirely.
    this.dispatchEvent(new DrawerOpenEvent());
  }

  #removeEventListeners() {
    const { panel } = this.refs;

    document.removeEventListener('keydown', this.#onKeyDown);
    panel.removeEventListener('click', this.#onBackdropClick);
  }

  /**
   * Moves focus to the close button inside the drawer panel.
   * Every theme-drawer renders the close button via theme-drawer-header.
   * If a future drawer omits it, that drawer is responsible for its own focus management.
   */
  #focusInitialElement() {
    const closeButton = /** @type {HTMLElement | null} */ (
      this.refs.panel.querySelector('.theme-drawer__close-button')
    );
    closeButton?.focus();
  }

  /**
   * Reopens a nested modal after the drawer panel changes mode so it remains
   * above the drawer in the browser top layer.
   *
   * @param {HTMLDialogElement} dialog - The nested dialog to restack.
   * @param {HTMLElement | null} focusTarget - Element to refocus inside the nested dialog.
   */
  #bringNestedDialogToFront(dialog, focusTarget) {
    dialog.close();
    dialog.showModal();

    if (focusTarget && document.contains(focusTarget) && dialog.contains(focusTarget)) {
      focusTarget.focus();
    }
  }

  /**
   * Slides the drawer out, waits for the animation, then closes the dialog.
   */
  async #closeDialog() {
    if (!this.isOpen) return;
    if (this.#isClosing) return;
    this.#isClosing = true;

    this.#removeEventListeners();
    removeTrapFocus();

    const { panel } = this.refs;

    this.removeAttribute('open');
    this.dispatchEvent(new DrawerCloseEvent());

    // Fade the backdrop out in step with the slide (this drawer is no longer [open], so it drops
    // out of the topmost calc; if it was the last one open, the backdrop deactivates).
    syncDrawerBackdrop();
    unlockScroll(panel);

    if (panel.open) {
      // Cancel any in-progress open animation before starting the close.
      panel.classList.remove('theme-drawer__dialog--opening', 'theme-drawer__dialog--opening-inline-start');

      panel.classList.add('theme-drawer__dialog--closing');
      await onAnimationEnd(panel, undefined, { subtree: false });
      panel.classList.remove('theme-drawer__dialog--closing');
    }

    panel.close();
    this.style.removeProperty('--drawer-stack-order');

    if (!document.querySelector('theme-drawer[open]')) {
      ThemeDrawer.#stackOrder = 0;
    }

    const trigger = this.#previouslyFocused;
    this.#previouslyFocused = null;
    // A Section Rendering API morph between open and close can replace the
    // trigger node. The JS reference stays valid but the element is detached
    // from the live DOM — and .focus() on a detached node is a silent no-op,
    // so we explicitly check and fall back to a fresh query.
    if (trigger && document.contains(trigger)) {
      trigger.focus();
    } else {
      /** @type {HTMLElement | null} */ (document.querySelector(`[aria-controls="${this.id}"]`))?.focus();
    }

    this.#isClosing = false;

    // Reconcile: if open() was called during the close animation, reopen now.
    if (this.#deferredOpen) {
      const options = this.#deferredOpen;
      this.#deferredOpen = false;
      this.open(options);
    }
  }

  /**
   * Closes the drawer.
   */
  async close() {
    // A tap that opens and closes again inside the same two frames must not leave a reveal armed.
    this.#isOpening = false;

    if (this.#isClosing) {
      // A close is already in progress — cancel any deferred open so the
      // drawer stays closed when the animation finishes.
      this.#deferredOpen = false;
      return;
    }
    await this.#closeDialog();
  }

  /**
   * Toggles the drawer. If the drawer is open but not the topmost in the
   * stack, it is brought to front instead of closed.
   */
  toggle() {
    // Tapped again before the reveal ran: cancel it, so a fast double tap still ends closed.
    if (this.#isOpening) {
      this.#isOpening = false;
      return;
    }
    if (this.isOpen && this.#isTopmost()) {
      return this.close();
    }
    return this.open();
  }

  /**
   * @returns {boolean} Whether this drawer has the highest stack order among all open drawers.
   */
  #isTopmost() {
    const myOrder = Number(this.style.getPropertyValue('--drawer-stack-order') || 0);
    for (const other of document.querySelectorAll('theme-drawer[open]')) {
      if (other === this) continue;
      const otherOrder = Number(/** @type {HTMLElement} */ (other).style.getPropertyValue('--drawer-stack-order') || 0);
      if (otherOrder > myOrder) return false;
    }
    return true;
  }
}

if (!customElements.get('theme-drawer')) {
  customElements.define('theme-drawer', ThemeDrawer);
}

export class DrawerOpenEvent extends CustomEvent {
  constructor() {
    super(DrawerOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:open';
}

export class DrawerCloseEvent extends CustomEvent {
  constructor() {
    super(DrawerCloseEvent.eventName, { bubbles: true });
  }

  static eventName = 'theme-drawer:close';
}
